// Screen 2 — Days & Meals per Day (wizard step 1 of 9)
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardScreen } from '../../components/planner/WizardScreen';
import { FieldError } from '../../components/planner/FieldError';
import { useDraft, STEP_ROUTES } from '../../lib/plannerDraft';

export default function Step2Days() {
  const nav = useNavigate();
  const { draft, patch, bump } = useDraft();
  const [showErr, setShowErr] = useState(false);

  const days = draft.days;
  const meals = draft.meals_per_day;
  const valid =
    Number.isInteger(days) && days >= 1 && days <= 5 &&
    Number.isInteger(meals) && meals >= 1 && meals <= 5;

  const onContinue = () => {
    if (!valid) { setShowErr(true); return; }
    bump(2);
    nav(STEP_ROUTES[2]);
  };

  return (
    <WizardScreen
      step={1}
      eyebrow="Getting started"
      title="How many days? How many meals?"
      canContinue={valid}
      onContinue={onContinue}
    >
      {/* Days -------------------------------------------------- */}
      <fieldset>
        <legend className="font-display uppercase tracking-wider text-sm mb-3" data-testid="days-label">
          Days
        </legend>
        <div
          role="radiogroup"
          aria-labelledby="days-label"
          className="grid grid-cols-5 gap-2"
        >
          {[1, 2, 3, 4, 5].map((n) => {
            const selected = days === n;
            return (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => patch({ days: n }, { invalidateGeneration: true })}
                data-testid={`days-${n}-btn`}
                className="font-display uppercase text-2xl h-14 transition-colors"
                style={{
                  background: selected ? 'var(--orange)' : '#fff',
                  color: selected ? '#fff' : 'var(--ink)',
                  border: selected ? '2px solid var(--orange)' : '1.5px solid var(--line)',
                  borderRadius: 'var(--radius)',
                  letterSpacing: '0.04em',
                }}
              >
                {n}
              </button>
            );
          })}
        </div>
        <FieldError id="days-error">
          {showErr && (!days) ? 'Please choose a number of days (1–5).' : null}
        </FieldError>
      </fieldset>

      {/* Meals per day ---------------------------------------- */}
      <fieldset className="mt-8">
        <legend className="font-display uppercase tracking-wider text-sm mb-3" data-testid="meals-label">
          Meals per day
        </legend>
        <div className="flex items-center gap-4" role="group" aria-labelledby="meals-label">
          <button
            type="button"
            aria-label="Decrease meals per day"
            onClick={() =>
              patch({ meals_per_day: Math.max(1, (meals || 1) - 1) }, { invalidateGeneration: true })
            }
            className="btn btn-outline"
            style={{ padding: 0, width: 48, height: 48 }}
            disabled={!meals || meals <= 1}
            data-testid="meals-decrement-btn"
          >
            −
          </button>
          <div
            className="font-display text-5xl min-w-[3.5rem] text-center"
            aria-live="polite"
            data-testid="meals-value"
          >
            {meals ?? '—'}
          </div>
          <button
            type="button"
            aria-label="Increase meals per day"
            onClick={() =>
              patch({ meals_per_day: Math.min(5, (meals || 0) + 1) }, { invalidateGeneration: true })
            }
            className="btn btn-outline"
            style={{ padding: 0, width: 48, height: 48 }}
            disabled={meals >= 5}
            data-testid="meals-increment-btn"
          >
            +
          </button>
          {!meals && (
            <button
              type="button"
              onClick={() => patch({ meals_per_day: 3 }, { invalidateGeneration: true })}
              className="text-sm underline underline-offset-4"
              style={{ color: 'var(--ink)' }}
              data-testid="meals-set-default-btn"
            >
              Start at 3
            </button>
          )}
        </div>
        <p className="mt-3 text-sm" style={{ color: 'var(--text-dim)' }}>
          You can change these any time before checkout.
        </p>
        <FieldError id="meals-error">
          {showErr && !meals ? 'Please choose meals per day (1–5).' : null}
        </FieldError>
      </fieldset>
    </WizardScreen>
  );
}
